import React, { useEffect, useState } from 'react';
import { Nav } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import './MainMenu.scss';
import TagManager from 'react-gtm-module';
import { CloseIcon } from '../common/CloseIcon';
import { Trans } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import FeedbackModal from '../FeedbackModal/FeedbackModal';
import FacebookIcon from '../common/SocialIcons/FacebookIcon';
import InstagramIcon from '../common/SocialIcons/InstagramIcon';
import LinkedInIcon from '../common/SocialIcons/LinkedInIcon';
import { SOCIAL_LINKS } from '../../config/socialLinks';
import { localStore } from '../utils/safeStorage';

type MainMenuProps = {
  isMainMenuPage?: boolean;
  isLoggedIn?: boolean;
  userEmail?: string;
};

const MainMenu = ({ isMainMenuPage, isLoggedIn = false, userEmail }: MainMenuProps) => {
  const { i18n } = useTranslation('mainMenu');
  const [wrapper, setWrapper] = useState('wrapper');
  const [mainMenuVisibility, setMainMenuVisibility] = useState(false);
  const languageIsEnglish = i18n.language.startsWith('en');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  const handleOpenClick = () => {
    setMainMenuVisibility(true);
  };

  const handleCloseClick = () => {
    setMainMenuVisibility(false);
  };

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    localStore.setItem('i18nextLng', lang);
  };

  useEffect(() => {
    if (mainMenuVisibility) {
      setWrapper(wrapper + ' open');
    } else {
      setWrapper('wrapper');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainMenuVisibility]);

  return (
    <div>
      <div
        className={`mainMenuOverlay ${mainMenuVisibility ? 'mainMenuOverlay--visible' : ''
          }`}
        onClick={() => handleCloseClick()}
      />

      <div className="main-menu-wrapper">
        <Nav defaultActiveKey="/home" className={`${wrapper} flex-column`}>
          <div className="closeMenu" onClick={() => handleCloseClick()}>
            <CloseIcon size={24} />
          </div>
          <Nav.Link
            className="menuElement"
            as={Link}
            to="/faq"
            onClick={() => {
              handleCloseClick();
              TagManager.dataLayer({
                dataLayer: {
                  event: 'what_is_jaakko_faq_clicked',
                  category: 'User Interaction',
                  action: 'Click',
                  label: 'What is Jaakko.com FAQ Clicked',
                },
              });
            }}
          >
            <Trans i18nKey="mainMenu.whatIsJaakko">
              What is Jaakko.com?
            </Trans>
          </Nav.Link>
          {!isMainMenuPage && (
            <Nav.Link
              className="menuElement"
              onClick={() => {
                TagManager.dataLayer({
                  dataLayer: {
                    event: 'main_menu_feedback_clicked',
                    category: 'User Interaction',
                    action: 'Click',
                    label: 'Main Menu Feedback Clicked',
                  },
                });
                handleCloseClick();
                setShowFeedbackModal(true);
              }}
            >
              <Trans i18nKey="mainMenu.feedback">Feedback</Trans>
            </Nav.Link>
          )}
          <Nav.Link
            className="menuElement"
            as={Link}
            to="/contact"
            onClick={() => handleCloseClick()}
          >
            <Trans i18nKey="mainMenu.contact">Contact</Trans>
          </Nav.Link>
          <Nav.Link
            className="menuElement"
            href={languageIsEnglish ? "https://info.jaakko.com/en-gb/terms_and_conditions" : "https://info.jaakko.com/kayttoehdot"}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleCloseClick()}
          >
            <Trans i18nKey="mainMenu.termsConditions">Terms & Conditions</Trans>
          </Nav.Link>
          <Nav.Link
            className="menuElement"
            href={languageIsEnglish ? "https://info.jaakko.com/en-gb/privacy_policy" : "https://info.jaakko.com/privacy_policy"}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleCloseClick()}
          >
            <Trans i18nKey="mainMenu.privacyPolicy">Privacy Policy</Trans>
          </Nav.Link>
          <Nav.Link
            className="menuElement"
            href={languageIsEnglish ? "https://info.jaakko.com/en-gb/cookies" : "https://info.jaakko.com/evasteet"}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleCloseClick()}
          >
            <Trans i18nKey="mainMenu.cookies">Cookies</Trans>
          </Nav.Link>

          <div className="menuElement main-menu-social-links">
            <a
              className={`main-menu-social-links__icon ${!SOCIAL_LINKS.facebook ? 'main-menu-social-links__icon--disabled' : ''}`}
              href={SOCIAL_LINKS.facebook || undefined}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              onClick={() => {
                TagManager.dataLayer({
                  dataLayer: {
                    event: 'main_menu_social_facebook_clicked',
                    category: 'User Interaction',
                    action: 'Click',
                    label: 'Main Menu Facebook Clicked',
                  },
                });
                handleCloseClick();
              }}
            >
              <FacebookIcon size={18} />
            </a>
            <a
              className={`main-menu-social-links__icon ${!SOCIAL_LINKS.linkedin ? 'main-menu-social-links__icon--disabled' : ''}`}
              href={SOCIAL_LINKS.linkedin || undefined}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              onClick={() => {
                TagManager.dataLayer({
                  dataLayer: {
                    event: 'main_menu_social_linkedin_clicked',
                    category: 'User Interaction',
                    action: 'Click',
                    label: 'Main Menu LinkedIn Clicked',
                  },
                });
                handleCloseClick();
              }}
            >
              <LinkedInIcon size={18} />
            </a>
            <a
              className={`main-menu-social-links__icon ${!SOCIAL_LINKS.instagram ? 'main-menu-social-links__icon--disabled' : ''}`}
              href={SOCIAL_LINKS.instagram || undefined}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              onClick={() => {
                TagManager.dataLayer({
                  dataLayer: {
                    event: 'main_menu_social_instagram_clicked',
                    category: 'User Interaction',
                    action: 'Click',
                    label: 'Main Menu Instagram Clicked',
                  },
                });
                handleCloseClick();
              }}
            >
              <InstagramIcon size={18} />
            </a>
          </div>

          <div className="menuElement language-selector">
            <div className="language-selector__title">
              <Trans i18nKey="mainMenu.language">Language</Trans>
            </div>
            <div className="language-selector__options">
              <button
                className={`language-selector__option ${languageIsEnglish ? 'language-selector__option--active' : ''}`}
                onClick={() => changeLanguage('en')}
              >
                EN
              </button>
              <button
                className={`language-selector__option ${!languageIsEnglish ? 'language-selector__option--active' : ''}`}
                onClick={() => changeLanguage('fi')}
              >
                FI
              </button>
            </div>
          </div>
        </Nav>
      </div>
      <div
        className={`menuBurger ${mainMenuVisibility ? 'menuBurger--open' : ''
          } ${isMainMenuPage ? 'menuBurger--isMainMenuPage' : ''}`}
        onClick={() =>
          mainMenuVisibility ? handleCloseClick() : handleOpenClick()
        }
      >
        <div className="burger-line"></div>
        <div className="burger-line"></div>
        <div className="burger-line"></div>
      </div>

      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        isLoggedIn={isLoggedIn}
        userEmail={userEmail}
      />
    </div>
  );
};

export default MainMenu;
